/**
 * Step 2 — Configure Bid Optimizer variables.
 * Themed rebuild of the old StepTwo.tsx; keeps the same header
 * auto-matching heuristics and the same per-column value validation
 * (age range / gender / locale / region / device platform / OS / device)
 * against DMAList/RegionsList/LocaleList — only the chrome changed.
 */

import React, { useEffect, useMemo, useState } from "react";
import Papa from "papaparse";
import { cloneDeep } from "lodash";
import { UploadStepData } from "./StepUpload";
import { DMA_LIST } from "../../../../constants/DMAList";
import { REGIONS_LIST } from "../../../../constants/RegionsList";
import { LOCALE_LIST } from "../../../../constants/LocaleList";

export interface ConfigureStepData {
  name: string;
  campaignObjective: string;
  userId: string;
  targetVariable: string;
  age?: string;
  gender?: string;
  locale?: string;
  geoLocationType?: string;
  geoLocation?: string;
  devicePlatform?: string;
  userOs?: string;
  userDevice?: string;
}

interface StepConfigureProps {
  uploadData: UploadStepData;
  existingData?: ConfigureStepData | null;
  onNext: (data: ConfigureStepData) => void;
  onPrevious: () => void;
  onNameChange?: (name: string) => void;
}

const CAMPAIGN_OBJECTIVES = [
  { label: "Sales", value: "sales" },
  { label: "Lead Generation", value: "lead" },
  { label: "Reach", value: "reach" },
];

const GEO_TYPES = [
  { label: "Designated Market Area (DMA)", value: "dma" },
  { label: "Regions", value: "region" },
];

function findMatchingHeader(headers: string[], searchTerms: string[]): string | undefined {
  for (const term of searchTerms) {
    const exact = headers.find(h => h.toLowerCase() === term.toLowerCase());
    if (exact) return exact;
  }
  for (const term of searchTerms) {
    const contains = headers.find(h => h.toLowerCase().includes(term.toLowerCase()));
    if (contains) return contains;
  }
  for (const term of searchTerms) {
    const word = headers.find(h => {
      const headerWords = h.toLowerCase().split(/\s+|_|-/);
      const termWords = term.toLowerCase().split(/\s+|_|-/);
      return termWords.some(w => headerWords.includes(w));
    });
    if (word) return word;
  }
  return undefined;
}

const StepConfigure: React.FC<StepConfigureProps> = ({ uploadData, existingData, onNext, onPrevious, onNameChange }) => {
  const { headers, dataFile, previewData } = uploadData;

  const [values, setValues] = useState<ConfigureStepData>(() => existingData || { name: "", campaignObjective: "lead", userId: "", targetVariable: "" });
  const [validationError, setValidationError] = useState<string | null>(null);
  const [validationWarnings, setValidationWarnings] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  // Preselect columns by header-name heuristics, same as the old flow.
  useEffect(() => {
    if (existingData) return;
    const userId = findMatchingHeader(headers, ["user id", "userid", "user_id", "id", "customer id", "customer_id", "customerid", "email"]);
    const targetVariable = findMatchingHeader(headers, ["target", "target variable", "target_variable", "sales", "revenue", "conversion"]);
    const age = findMatchingHeader(headers, ["age", "age range", "age_range", "agerange"]);
    const gender = findMatchingHeader(headers, ["gender", "sex"]);
    const locale = findMatchingHeader(headers, ["locale", "language", "country"]);
    const geoLocation = findMatchingHeader(headers, ["dma", "region", "location", "geo", "geo location", "geo_location", "geolocation"]);
    const devicePlatform = findMatchingHeader(headers, ["device platform", "device_platform", "deviceplatform", "platform", "device type"]);
    const userOs = findMatchingHeader(headers, ["user os", "user_os", "useros", "os", "operating system", "system"]);
    const userDevice = findMatchingHeader(headers, ["user device", "user_device", "userdevice", "device", "device name", "device_name"]);

    let geoLocationType: string | undefined;
    if (geoLocation) {
      geoLocationType = geoLocation.toLowerCase().includes("region") ? "region" : "dma";
    }

    setValues(v => ({
      ...v,
      geoLocationType,
      ...(userId ? { userId } : {}),
      ...(targetVariable ? { targetVariable } : {}),
      ...(age ? { age } : {}),
      ...(gender ? { gender } : {}),
      ...(locale ? { locale } : {}),
      ...(geoLocation ? { geoLocation } : {}),
      ...(devicePlatform ? { devicePlatform } : {}),
      ...(userOs ? { userOs } : {}),
      ...(userDevice ? { userDevice } : {}),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headers]);

  const selectedColumns = useMemo(() => {
    const m: Record<string, string> = {};
    Object.entries(values).forEach(([key, value]) => {
      if (value && headers.includes(value as string)) m[value as string] = key;
    });
    return m;
  }, [values, headers]);

  const set = (key: keyof ConfigureStepData, value: string) => {
    setValues(v => ({ ...v, [key]: value }));
    if (key === "name") onNameChange?.(value);
  };

  // ── Value validation against the actual CSV data (same regexes/lists as old StepTwo) ──

  const colIndex = (results: any, col: string) => results.data[0].indexOf(col);

  const validateAge = (results: any, col: string): boolean => {
    const r = cloneDeep(results);
    const idx = colIndex(r, col);
    r.data.shift();
    const re = /^(\d+-\d+|\d+\+|default)$/;
    for (const row of r.data) {
      if (!re.test(row[idx])) { setValidationError(`Unsupported age value: ${row[idx]}`); return false; }
    }
    return true;
  };
  const validateGender = (results: any, col: string): boolean => {
    const r = cloneDeep(results);
    const idx = colIndex(r, col);
    r.data.shift();
    const re = /^(male|female|default)$/;
    for (const row of r.data) {
      if (!re.test(String(row[idx]).toLowerCase())) { setValidationError(`Unsupported gender value: ${row[idx]}`); return false; }
    }
    return true;
  };
  const validateLocale = (results: any, col: string): boolean => {
    const r = cloneDeep(results);
    const idx = colIndex(r, col);
    r.data.shift();
    const names = LOCALE_LIST.map(i => i.name);
    for (const row of r.data) {
      if (!names.includes(row[idx])) { setValidationError(`Unsupported locale value: ${row[idx]}`); return false; }
    }
    return true;
  };
  const validateDevicePlatform = (results: any, col: string): boolean => {
    const r = cloneDeep(results);
    const idx = colIndex(r, col);
    r.data.shift();
    const re = /^(mobile|desktop)$/;
    for (const row of r.data) {
      if (!re.test(String(row[idx]).toLowerCase())) { setValidationError(`Unsupported device platform value: ${row[idx]}`); return false; }
    }
    return true;
  };
  const validateUserOs = (results: any, col: string): boolean => {
    const r = cloneDeep(results);
    const idx = colIndex(r, col);
    r.data.shift();
    const re = /^(Android|Windows|iOS|Windows Phone|default)$/;
    for (const row of r.data) {
      if (!re.test(row[idx])) { setValidationError(`Unsupported user OS value: ${row[idx]}`); return false; }
    }
    return true;
  };
  const validateUserDevice = (results: any, col: string): boolean => {
    const r = cloneDeep(results);
    const idx = colIndex(r, col);
    r.data.shift();
    const re = /^(iPad|iPhone|default)$/;
    for (const row of r.data) {
      if (!re.test(row[idx])) { setValidationError(`Unsupported user device value: ${row[idx]}`); return false; }
    }
    return true;
  };
  const validateRegion = (results: any, col: string, geoType: string): boolean => {
    const r = cloneDeep(results);
    const idx = colIndex(r, col);
    r.data.shift();
    const names = (geoType === "dma" ? DMA_LIST : REGIONS_LIST).map(i => i.name);
    for (const row of r.data) {
      if (!names.includes(row[idx])) { setValidationError(`Unsupported ${geoType === "dma" ? "DMA" : "Region"} value: ${row[idx]}`); return false; }
    }
    return true;
  };

  const handleSubmit = () => {
    setLoading(true);
    setValidationError(null);
    setValidationWarnings([]);

    if (!values.name) { setValidationError("Please enter a job name"); setLoading(false); return; }
    if (!values.campaignObjective) { setValidationError("Please select a campaign objective"); setLoading(false); return; }
    if (!values.userId || !values.targetVariable) {
      setValidationError("Customer Identifier and Target Variable are required");
      setLoading(false);
      return;
    }

    Papa.parse(dataFile, {
      complete: results => {
        const warnings: string[] = [];
        if (!values.age && !values.gender && !values.locale && !values.geoLocation) {
          warnings.push("No demographic or geographic variables selected. This may reduce optimization effectiveness.");
        }

        if (values.geoLocation && values.geoLocationType) {
          if (!validateRegion(results, values.geoLocation, values.geoLocationType)) { setLoading(false); return; }
        } else if (values.geoLocation && !values.geoLocationType) {
          setValidationError("Please select a Geo Location Type when Geo Location is provided");
          setLoading(false);
          return;
        }
        if (values.age && !validateAge(results, values.age)) { setLoading(false); return; }
        if (values.locale && !validateLocale(results, values.locale)) { setLoading(false); return; }
        if (values.gender && !validateGender(results, values.gender)) { setLoading(false); return; }
        if (values.devicePlatform && !validateDevicePlatform(results, values.devicePlatform)) { setLoading(false); return; }
        if (values.userOs && !validateUserOs(results, values.userOs)) { setLoading(false); return; }
        if (values.userDevice && !validateUserDevice(results, values.userDevice)) { setLoading(false); return; }

        setValidationWarnings(warnings);
        setLoading(false);
        onNext(values);
      },
      error: () => { setValidationError("Error parsing CSV file"); setLoading(false); },
    });
  };

  const ColumnSelect: React.FC<{ value?: string; onChange: (v: string) => void; allowClear?: boolean }> = ({ value, onChange, allowClear }) => (
    <select
      value={value || ""}
      onChange={e => onChange(e.target.value)}
      style={selectStyle}
    >
      <option value="">{allowClear ? "— None —" : "Select column"}</option>
      {headers.map(h => <option key={h} value={h}>{h}</option>)}
    </select>
  );

  return (
    <div style={{ maxWidth: 920 }}>
      <h2 style={{ fontSize: 18, fontWeight: 600, color: "var(--text-primary)", margin: "0 0 6px" }}>
        Configure Bid Optimizer
      </h2>
      <p style={{ fontSize: 13, color: "var(--text-secondary)", margin: "0 0 20px", lineHeight: 1.5 }}>
        Select the variables from your data that will be used for bid optimization. Required fields are marked with an asterisk (*).
      </p>

      <Section title="Basic Information">
        <Row>
          <Field label="Job Name *" hint="Name of the job to identify in the jobs list">
            <input value={values.name} onChange={e => set("name", e.target.value)} placeholder="Enter job name" style={inputStyle} maxLength={100} />
          </Field>
          <Field label="Campaign Objective *" hint="Objective of the campaign for better reach and cost optimization">
            <select value={values.campaignObjective} onChange={e => set("campaignObjective", e.target.value)} style={selectStyle}>
              {CAMPAIGN_OBJECTIVES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
        </Row>
      </Section>

      <Section title="Required Variables" badge="Required" badgeColor="var(--blue)">
        <Row>
          <Field label="Customer Identifier *" hint="Customer Id for unique identification of records. Eg: Id or email">
            <ColumnSelect value={values.userId} onChange={v => set("userId", v)} />
          </Field>
          <Field label="Target Variable *" hint="Column that the model optimizes against. Eg: Sales">
            <ColumnSelect value={values.targetVariable} onChange={v => set("targetVariable", v)} />
          </Field>
        </Row>
      </Section>

      <Section title="Demographic Variables" badge="Recommended" badgeColor="#10b981">
        <Row cols={3}>
          <Field label="Age Range" hint="Values like '18-24', '25-34', or 'default'">
            <ColumnSelect value={values.age} onChange={v => set("age", v)} allowClear />
          </Field>
          <Field label="Gender" hint="Values: 'male', 'female', or 'default'">
            <ColumnSelect value={values.gender} onChange={v => set("gender", v)} allowClear />
          </Field>
          <Field label="Locale" hint="Locale for each record">
            <ColumnSelect value={values.locale} onChange={v => set("locale", v)} allowClear />
          </Field>
        </Row>
      </Section>

      <Section title="Geographic Variables" badge="Recommended" badgeColor="#10b981">
        <Row>
          <Field label="Geo Location Type" hint="Select the geo location type">
            <select value={values.geoLocationType || ""} onChange={e => set("geoLocationType", e.target.value)} style={selectStyle}>
              <option value="">— None —</option>
              {GEO_TYPES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
          <Field label="Geo Location" hint="Column with DMA/Region for each record">
            <ColumnSelect value={values.geoLocation} onChange={v => set("geoLocation", v)} allowClear />
          </Field>
        </Row>
      </Section>

      <Section title="Device Variables" badge="Optional" badgeColor="#f59e0b">
        <Row cols={3}>
          <Field label="Device Platform" hint="Values: 'mobile' or 'desktop'">
            <ColumnSelect value={values.devicePlatform} onChange={v => set("devicePlatform", v)} allowClear />
          </Field>
          <Field label="User OS" hint="Values: 'Android', 'Windows', 'iOS', 'Windows Phone', or 'default'">
            <ColumnSelect value={values.userOs} onChange={v => set("userOs", v)} allowClear />
          </Field>
          <Field label="User Device" hint="Values: 'iPad', 'iPhone', or 'default'">
            <ColumnSelect value={values.userDevice} onChange={v => set("userDevice", v)} allowClear />
          </Field>
        </Row>
      </Section>

      <Section title="Data Preview">
        <div style={{ overflowX: "auto", border: "1px solid var(--border-subtle)", borderRadius: "var(--radius)", maxHeight: 240 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr>
                {headers.map(h => (
                  <th key={h} style={{
                    position: "sticky", top: 0, textAlign: "left", padding: "8px 10px",
                    background: selectedColumns[h] ? "var(--blue-subtle)" : "var(--bg-hover)",
                    color: selectedColumns[h] ? "var(--blue)" : "var(--text-secondary)",
                    borderBottom: "1px solid var(--border-default)", whiteSpace: "nowrap",
                  }}>
                    {h}{selectedColumns[h] ? " ✓" : ""}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {previewData.map((row, i) => (
                <tr key={i}>
                  {headers.map(h => (
                    <td key={h} style={{
                      padding: "7px 10px", color: "var(--text-primary)",
                      background: selectedColumns[h] ? "rgba(59,130,246,0.06)" : "transparent",
                      borderBottom: "1px solid var(--border-subtle)", whiteSpace: "nowrap",
                    }}>
                      {String(row[h] ?? "")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {validationError && (
        <div style={{ marginTop: 16, padding: "10px 14px", borderRadius: "var(--radius)", background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.3)", color: "#ef4444", fontSize: 12 }}>
          {validationError}
        </div>
      )}
      {validationWarnings.length > 0 && (
        <div style={{ marginTop: 16, padding: "10px 14px", borderRadius: "var(--radius)", background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.3)", color: "#f59e0b", fontSize: 12 }}>
          {validationWarnings.map((w, i) => <div key={i}>• {w}</div>)}
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 24 }}>
        <button onClick={onPrevious} disabled={loading} style={secondaryBtnStyle}>← Previous</button>
        <button onClick={handleSubmit} disabled={loading} style={primaryBtnStyle}>
          {loading ? "Validating…" : "Next →"}
        </button>
      </div>
    </div>
  );
};

// ── Shared layout bits ───────────────────────────────────────────────────────

const Section: React.FC<{ title: string; badge?: string; badgeColor?: string; children: React.ReactNode }> = ({ title, badge, badgeColor, children }) => (
  <div style={{ marginBottom: 20, padding: 18, borderRadius: "var(--radius)", background: "var(--bg-surface)", border: "1px solid var(--border-subtle)" }}>
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
      <span style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text-primary)" }}>{title}</span>
      {badge && (
        <span style={{ fontSize: 10, fontWeight: 600, color: "#fff", background: badgeColor, padding: "2px 7px", borderRadius: 10 }}>
          {badge}
        </span>
      )}
    </div>
    {children}
  </div>
);

const Row: React.FC<{ cols?: number; children: React.ReactNode }> = ({ cols = 2, children }) => (
  <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 16 }}>{children}</div>
);

const Field: React.FC<{ label: string; hint?: string; children: React.ReactNode }> = ({ label, hint, children }) => (
  <div>
    <label style={{ display: "block", fontSize: 12, fontWeight: 500, color: "var(--text-secondary)", marginBottom: 5 }} title={hint}>
      {label}
    </label>
    {children}
    {hint && <div style={{ fontSize: 10.5, color: "var(--text-tertiary)", marginTop: 3 }}>{hint}</div>}
  </div>
);

const inputStyle: React.CSSProperties = {
  width: "100%", padding: "8px 10px", borderRadius: "var(--radius-sm)",
  border: "1px solid var(--border-default)", background: "var(--bg-base)",
  color: "var(--text-primary)", fontSize: 13, fontFamily: "var(--font-sans)",
};
const selectStyle: React.CSSProperties = { ...inputStyle, cursor: "pointer" };
const primaryBtnStyle: React.CSSProperties = {
  padding: "9px 20px", borderRadius: "var(--radius)", background: "var(--blue)",
  color: "#fff", border: "none", fontSize: 13, fontWeight: 500, cursor: "pointer",
};
const secondaryBtnStyle: React.CSSProperties = {
  padding: "9px 20px", borderRadius: "var(--radius)", background: "var(--bg-hover)",
  color: "var(--text-secondary)", border: "1px solid var(--border-default)", fontSize: 13, cursor: "pointer",
};

export default StepConfigure;
